import { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useChat } from '../context/ChatContext';
import { getBackendUrl } from '../config/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import Navbar from '../components/Navbar';
import {
  MessageCircle, Send, ArrowLeft, Search, User, Check, CheckCheck,
  Trash2, X, Package, Plus,
} from 'lucide-react';

export default function MessagesPage() {
  const { token, user } = useAuth();
  const { fetchUnreadCount } = useChat();

  const [conversations, setConversations] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showNewChat, setShowNewChat] = useState(false);

  const messagesEndRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const fetchConversations = useCallback(async () => {
    if (!token) return;
    try {
      const resp = await fetch(`${getBackendUrl()}/api/messages/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (resp.ok) {
        const data = await resp.json();
        setConversations(data.conversations);
      }
    } catch (e) { console.error('Error:', e); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { if (token) fetchConversations(); }, [token, fetchConversations]);
  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  const fetchMessages = useCallback(async (partnerId) => {
    try {
      const resp = await fetch(`${getBackendUrl()}/api/messages/${partnerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (resp.ok) {
        const data = await resp.json();
        setMessages(data.messages);
        setSelectedPartner(data.partner);
        fetchUnreadCount();
        fetchConversations();
      }
    } catch (e) { console.error('Error:', e); }
  }, [token, fetchUnreadCount, fetchConversations]);

  // Poll for updates
  useEffect(() => {
    if (!token || !selectedPartner?.id) return;
    const interval = setInterval(() => fetchMessages(selectedPartner.id), 5000);
    return () => clearInterval(interval);
  }, [token, selectedPartner?.id, fetchMessages]);

  // Listen for WebSocket events
  useEffect(() => {
    const handler = (e) => {
      const msg = e.detail;
      if (selectedPartner && (msg.sender_id === selectedPartner.id || msg.recipient_id === selectedPartner.id)) {
        setMessages(prev => prev.some(m => m.id === msg.id) ? prev : [...prev, msg]);
        if (msg.sender_id === selectedPartner.id) {
          fetch(`${getBackendUrl()}/api/messages/${selectedPartner.id}/read`, {
            method: 'PUT', headers: { Authorization: `Bearer ${token}` }
          }).catch(() => {});
        }
      }
      fetchConversations();
    };
    window.addEventListener('chat-new-message', handler);
    return () => window.removeEventListener('chat-new-message', handler);
  }, [selectedPartner, token, fetchConversations]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedPartner || sending) return;
    setSending(true);
    try {
      const resp = await fetch(`${getBackendUrl()}/api/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ recipient_id: selectedPartner.id, content: newMessage.trim() })
      });
      if (resp.ok) {
        const data = await resp.json();
        setMessages(prev => [...prev, data.message]);
        setNewMessage('');
        fetchConversations();
      }
    } catch (e) { console.error('Error:', e); }
    finally { setSending(false); }
  };

  const deleteMessage = async (messageId) => {
    try {
      const resp = await fetch(`${getBackendUrl()}/api/messages/${messageId}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` }
      });
      if (resp.ok) setMessages(prev => prev.filter(m => m.id !== messageId));
    } catch (e) { console.error('Error:', e); }
  };

  const searchUsers = async (query) => {
    if (!query.trim()) { setSearchResults([]); return; }
    try {
      const resp = await fetch(`${getBackendUrl()}/api/messages/search-users?q=${encodeURIComponent(query)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (resp.ok) {
        const data = await resp.json();
        setSearchResults(data.users);
      }
    } catch (e) { console.error('Error:', e); }
  };

  const startConversation = (partner) => {
    setSelectedPartner({ id: partner.id, name: partner.username || partner.name, avatar_url: partner.avatar_url });
    setMessages([]);
    setShowNewChat(false);
    setSearchQuery('');
    setSearchResults([]);
  };

  const formatTime = (dateStr) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - d) / 86400000);
    if (diff === 0) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (diff === 1) return 'Yesterday';
    if (diff < 7) return d.toLocaleDateString([], { weekday: 'short' });
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-zinc-900 via-zinc-900 to-zinc-800 flex items-center justify-center">
        <div className="text-center">
          <MessageCircle className="w-16 h-16 mx-auto mb-4 text-zinc-600" />
          <h2 className="text-xl font-bold mb-2">Sign in to message</h2>
          <p className="text-zinc-400">Log in to send and receive messages</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-900 via-zinc-900 to-zinc-800">
      <Navbar />
      <div className="pt-20 px-4 pb-4">
        <div className="max-w-6xl mx-auto">
          <div className="glass rounded-2xl overflow-hidden border border-white/10" style={{ height: 'calc(100vh - 120px)' }} data-testid="messages-page">
            <div className="flex h-full">
              {/* Conversations List */}
              <div className={`w-full md:w-80 border-r border-white/10 flex flex-col ${selectedPartner ? 'hidden md:flex' : 'flex'}`}>
                <div className="p-4 border-b border-white/10">
                  <div className="flex items-center justify-between mb-3">
                    <h1 className="text-lg font-bold flex items-center gap-2">
                      <MessageCircle className="w-5 h-5 text-cyan-400" />
                      Messages
                    </h1>
                    <Button variant="outline" size="sm" onClick={() => setShowNewChat(!showNewChat)} className="border-white/10 text-xs" data-testid="msg-new-chat-btn">
                      {showNewChat ? <X className="w-4 h-4" /> : <><Plus className="w-3.5 h-3.5 mr-1" /> New</>}
                    </Button>
                  </div>

                  {showNewChat && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} className="overflow-hidden">
                      <div className="relative mb-2">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                        <Input
                          placeholder="Search users..."
                          value={searchQuery}
                          onChange={(e) => { setSearchQuery(e.target.value); searchUsers(e.target.value); }}
                          className="pl-10 bg-white/5 border-white/10 text-sm"
                          autoFocus
                          data-testid="msg-user-search"
                        />
                      </div>
                      {searchResults.length > 0 && (
                        <div className="bg-white/5 rounded-lg max-h-40 overflow-y-auto">
                          {searchResults.map((u) => (
                            <button key={u.id} onClick={() => startConversation(u)} className="w-full p-3 flex items-center gap-3 hover:bg-white/5 transition-colors text-left" data-testid={`msg-user-result-${u.id}`}>
                              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center">
                                <User className="w-4 h-4 text-white" />
                              </div>
                              <span className="font-medium text-sm">{u.username || u.name}</span>
                            </button>
                          ))}
                        </div>
                      )}
                      {searchQuery && searchResults.length === 0 && (
                        <p className="text-xs text-zinc-500 text-center py-2">No users found.</p>
                      )}
                    </motion.div>
                  )}
                </div>

                <div className="flex-1 overflow-y-auto">
                  {loading ? (
                    <div className="p-4 text-center text-zinc-500 text-sm">Loading...</div>
                  ) : conversations.length === 0 ? (
                    <div className="p-8 text-center">
                      <MessageCircle className="w-12 h-12 mx-auto mb-3 text-zinc-600" />
                      <p className="text-zinc-400 mb-2 text-sm">No conversations yet</p>
                      <p className="text-xs text-zinc-500">Start by messaging someone!</p>
                    </div>
                  ) : (
                    conversations.map((conv) => (
                      <button
                        key={conv.partner_id}
                        onClick={() => fetchMessages(conv.partner_id)}
                        className={`w-full p-4 flex items-center gap-3 hover:bg-white/5 transition-colors border-b border-white/5 ${selectedPartner?.id === conv.partner_id ? 'bg-white/10' : ''}`}
                        data-testid={`msg-conversation-${conv.partner_id}`}
                      >
                        <div className="relative">
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center">
                            {conv.partner_avatar ? (
                              <img src={conv.partner_avatar} alt="" className="w-full h-full rounded-full object-cover" />
                            ) : (
                              <User className="w-6 h-6 text-white" />
                            )}
                          </div>
                          {conv.unread_count > 0 && (
                            <span className="absolute -top-1 -right-1 min-w-[20px] h-5 bg-cyan-500 rounded-full text-xs flex items-center justify-center font-bold px-1">
                              {conv.unread_count}
                            </span>
                          )}
                        </div>
                        <div className="flex-1 text-left min-w-0">
                          <div className="flex items-center justify-between">
                            <span className="font-semibold truncate text-sm">{conv.partner_name}</span>
                            <span className="text-xs text-zinc-500">{formatTime(conv.last_message_at)}</span>
                          </div>
                          <p className="text-xs text-zinc-400 truncate">{conv.last_message}</p>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </div>

              {/* Chat Area */}
              <div className={`flex-1 flex flex-col ${!selectedPartner ? 'hidden md:flex' : 'flex'}`}>
                {selectedPartner ? (
                  <>
                    <div className="p-4 border-b border-white/10 flex items-center gap-3">
                      <Button variant="ghost" size="sm" onClick={() => setSelectedPartner(null)} className="md:hidden">
                        <ArrowLeft className="w-5 h-5" />
                      </Button>
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center">
                        {selectedPartner.avatar_url ? (
                          <img src={selectedPartner.avatar_url} alt="" className="w-full h-full rounded-full object-cover" />
                        ) : (
                          <User className="w-5 h-5 text-white" />
                        )}
                      </div>
                      <span className="font-bold">{selectedPartner.name}</span>
                    </div>

                    <div className="flex-1 overflow-y-auto p-4 space-y-3">
                      {messages.length === 0 ? (
                        <div className="text-center py-12 text-zinc-500">
                          <p className="text-sm">No messages yet. Say hello!</p>
                        </div>
                      ) : (
                        messages.map((msg) => {
                          const isOwn = msg.sender_id === user.id;
                          return (
                            <motion.div key={msg.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                              <div className={`group max-w-[75%]`}>
                                {msg.listing_context && (
                                  <div className="mb-1 px-3 py-1.5 bg-white/5 rounded-lg text-xs flex items-center gap-2">
                                    <Package className="w-3 h-3 text-cyan-400" />
                                    <span className="truncate text-zinc-400">Re: {msg.listing_context.title}</span>
                                  </div>
                                )}
                                <div className={`px-4 py-2 rounded-2xl ${isOwn ? 'bg-cyan-500 text-white rounded-br-md' : 'bg-white/10 rounded-bl-md'}`}>
                                  <p className="break-words text-sm">{msg.content}</p>
                                </div>
                                <div className={`flex items-center gap-2 mt-1 text-xs text-zinc-500 ${isOwn ? 'justify-end' : ''}`}>
                                  <span>{formatTime(msg.created_at)}</span>
                                  {isOwn && (
                                    <>
                                      {msg.read ? <CheckCheck className="w-3 h-3 text-cyan-400" /> : <Check className="w-3 h-3" />}
                                      <button onClick={() => deleteMessage(msg.id)} className="opacity-0 group-hover:opacity-100 transition-opacity text-zinc-500 hover:text-rose-400">
                                        <Trash2 className="w-3 h-3" />
                                      </button>
                                    </>
                                  )}
                                </div>
                              </div>
                            </motion.div>
                          );
                        })
                      )}
                      <div ref={messagesEndRef} />
                    </div>

                    <form onSubmit={sendMessage} className="p-4 border-t border-white/10">
                      <div className="flex gap-3">
                        <Input
                          placeholder="Type a message..."
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          className="flex-1 bg-white/5 border-white/10"
                          maxLength={2000}
                          data-testid="msg-input"
                        />
                        <Button type="submit" disabled={!newMessage.trim() || sending} className="bg-cyan-500 hover:bg-cyan-600" data-testid="msg-send-btn">
                          <Send className="w-5 h-5" />
                        </Button>
                      </div>
                    </form>
                  </>
                ) : (
                  <div className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                      <MessageCircle className="w-16 h-16 mx-auto mb-4 text-zinc-600" />
                      <h2 className="text-xl font-bold mb-2">Select a conversation</h2>
                      <p className="text-zinc-400 text-sm">Choose someone to start chatting</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
